if (!customElements.get('rr-recent-products')) {
  customElements.define('rr-recent-products', class extends HTMLElement {
    key = 'rr:recent-products:v1';
    generation = 0;
    recorded = false;
    near = false;
    controller = null;
    valid(item, now = Date.now()) {
      return item && /^\d{1,20}$/.test(String(item.id)) && typeof item.handle === 'string' && item.handle.length > 0 && item.handle.length <= 512 && !['.', '..'].includes(item.handle) && !/[\s\/%?#\\\u0000-\u001f\u007f]/u.test(item.handle) && Number.isFinite(item.at) && item.at > now - 30 * 86400000 && item.at <= now + 300000;
    }
    permitted() {
      return window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.() === true;
    }
    read() {
      try {
        const raw = JSON.parse(localStorage.getItem(this.key) || '[]');
        if (!Array.isArray(raw)) return [];
        const seen = new Set();
        return raw.filter(item => this.valid(item)).sort((a, b) => b.at - a.at).filter(item => {
          if (seen.has(String(item.id))) return false;
          seen.add(String(item.id));
          return true;
        }).slice(0, 12).map(({id, handle, at}) => ({id: String(id), handle, at}));
      } catch { return []; }
    }
    connectedCallback() {
      this.dataset.state = 'initializing';
      this.panel = this.querySelector('.rr-recent-products__content');
      this.grid = this.querySelector('ul');
      this.status = this.querySelector('[role="status"]');
      this.onConsent = () => this.sync({purgeDenied: true});
      this.onStorage = event => { if (event.key === this.key) this.sync({record: false}); };
      this.onClear = () => this.clear();
      this.querySelector('button').addEventListener('click', this.onClear);
      document.addEventListener('visitorConsentCollected', this.onConsent);
      window.addEventListener('storage', this.onStorage);
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.near = true;
          this.observer.disconnect();
          this.sync();
        }
      }, {rootMargin: '600px 0px'});
      this.observer.observe(this);
      if (window.Shopify?.customerPrivacy) this.sync({purgeDenied: true});
      else window.Shopify?.loadFeatures?.([{name: 'consent-tracking-api', version: '0.1'}], error => {
        if (!error && this.isConnected) this.sync({purgeDenied: true});
      });
    }
    disconnectedCallback() {
      this.cancel();
      this.observer?.disconnect();
      document.removeEventListener('visitorConsentCollected', this.onConsent);
      window.removeEventListener('storage', this.onStorage);
      this.querySelector('button')?.removeEventListener('click', this.onClear);
    }
    cancel() {
      this.generation++;
      this.controller?.abort();
    }
    clear() {
      this.cancel();
      try { localStorage.removeItem(this.key); } catch {}
      this.recorded = true;
      this.dataset.state = 'cleared';
      this.panel.hidden = true;
      this.grid.replaceChildren();
      this.status.textContent = this.dataset.cleared;
      const heading = document.querySelector('main h1');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus({preventScroll: true});
      }
    }
    async sync({record = true, purgeDenied = false} = {}) {
      this.cancel();
      if (!this.permitted()) {
        this.dataset.state = 'consent-denied';
        // Only a fresh consent decision/initialization may purge history. A
        // background tab can hold a stale denied snapshot after another tab accepts.
        if (purgeDenied) try { localStorage.removeItem(this.key); } catch {}
        this.recorded = false;
        this.panel.hidden = true;
        this.grid.replaceChildren();
        return;
      }
      let history = this.read();
      const current = {id: this.dataset.productId, handle: this.dataset.productHandle, at: Date.now()};
      if (record && !this.recorded && this.valid(current)) {
        history = [current, ...history.filter(item => item.id !== current.id)].slice(0, 12);
        try { localStorage.setItem(this.key, JSON.stringify(history)); this.recorded = true; } catch { this.dataset.state = 'storage-unavailable'; return; }
      }
      if (!this.near) { this.dataset.state = 'deferred'; return; }
      const items = history.filter(item => item.id !== current.id).slice(0, Math.min(Number(this.dataset.limit) || 4, 4));
      if (!items.length) {
        this.dataset.state = 'empty';
        this.panel.hidden = true;
        this.grid.replaceChildren();
        return;
      }
      const generation = this.generation;
      this.dataset.state = 'loading';
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const cards = await Promise.all(items.map(async item => {
        try {
          const url = new URL(this.dataset.root + 'products/' + encodeURIComponent(item.handle), location.origin);
          if (url.origin !== location.origin) return null;
          url.searchParams.set('section_id', 'rr-recent-card');
          const response = await fetch(url, {signal, credentials: 'same-origin'});
          if (!response.ok || new URL(response.url).origin !== location.origin) return null;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const card = html.querySelector('[data-rr-recent-id]');
          if (card?.dataset.rrRecentId !== item.id) return null;
          const link = card.querySelector('a[href]');
          if (!link) return null;
          const linkUrl = new URL(link.getAttribute('href'), location.origin);
          if (linkUrl.origin !== location.origin || decodeURIComponent(linkUrl.pathname) !== decodeURIComponent(url.pathname)) return null;
          return card;
        } catch { return null; }
      }));
      if (!this.isConnected || signal.aborted || generation !== this.generation || !this.permitted()) return;
      const valid = cards.filter(Boolean);
      this.grid.replaceChildren(...valid);
      this.panel.hidden = !valid.length;
      this.dataset.state = valid.length ? 'ready' : 'no-results';
    }
  });
}
